"use client";

import posthog from "posthog-js";
import { clearBrowserAttributionStorage } from "@/components/analytics/browser-attribution";
import { AW_ID, GA_ID } from "@/lib/analytics/google-ids";
import { ANALYTICS_LOCATION_CHANGE_EVENT } from "@/lib/analytics/suppressed-paths";
import {
  WEB_CONSENT_COOKIE_MAX_AGE_SECONDS,
  WEB_CONSENT_COOKIE_NAMES,
  parseWebConsentSnapshot,
  type WebConsentCategory,
  type WebConsentSnapshot,
} from "@/lib/cookie-consent";
import { isOptionalCookieForCategory } from "@/lib/optional-analytics-cookies";

const MAX_BROWSER_TIMEOUT_MS = 2_147_000_000;
const CONSENT_CHANGE_EVENT = "cookie_consent_change";
const ADVERTISING_ATTRIBUTION_QUERY_KEYS = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_offer",
  "ref",
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "msclkid",
  "gad_source",
  "gad_campaignid",
  "fbclid",
]);
// Keep the established event name for compatibility. Its semantics are now
// v2: it is emitted by this lifecycle for timestamp renewals and expiry too.
export const WEB_CONSENT_LIFECYCLE_EVENT = CONSENT_CHANGE_EVENT;
export const WEB_CONSENT_REPROMPT_EVENT = "web_consent_reprompt";
/** Dispatched by any trigger (e.g. the footer link) to reopen the panel. */
export const OPEN_COOKIE_PREFERENCES_EVENT = "open_cookie_preferences";
/** Requests consumption of a newly signed authenticated consent handoff. */
export const ACCOUNT_CONSENT_HANDOFF_EVENT = "account_consent_handoff";

// CookieBanner loads via next/dynamic({ssr: false}) and only subscribes to
// OPEN_COOKIE_PREFERENCES_EVENT once its own effect runs, which can be after
// a separately-hydrated trigger (e.g. the footer link) is already clickable.
// A bare dispatch during that gap has no listener and is lost. This
// module-scoped flag survives the gap: the trigger sets it before dispatching,
// and CookieBanner checks it once on mount in addition to subscribing live.
let pendingOpenRequest = false;
let pendingAccountConsentHandoffRequest = false;

/** Call from any reopen trigger instead of dispatching the event directly. */
export function requestOpenCookiePreferences(): void {
  pendingOpenRequest = true;
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_PREFERENCES_EVENT));
}

/** CookieBanner-only: consumes and clears a request left pending before mount. */
export function consumePendingCookiePreferencesOpenRequest(): boolean {
  const pending = pendingOpenRequest;
  pendingOpenRequest = false;
  return pending;
}

/** Call after an explicit Account sync returns 202 with signed handoff state. */
export function requestAccountConsentHandoff(): void {
  pendingAccountConsentHandoffRequest = true;
  window.dispatchEvent(new CustomEvent(ACCOUNT_CONSENT_HANDOFF_EVENT));
}

/** AccountConsentBridge-only: consumes a request dispatched before its mount. */
export function consumePendingAccountConsentHandoffRequest(): boolean {
  const pending = pendingAccountConsentHandoffRequest;
  pendingAccountConsentHandoffRequest = false;
  return pending;
}

export type WebConsentLifecycleReason =
  "initial" | "explicit" | "storage" | "focus" | "visibility" | "expiry";

export interface WebConsentLifecycleUpdate {
  snapshot: WebConsentSnapshot;
  reason: WebConsentLifecycleReason;
}

export function isAdvertisingAttributionPropertyKey(key: string): boolean {
  const normalized = key
    .trim()
    .toLowerCase()
    .replace(/^\$+/, "")
    .replace(/^initial_/, "");
  return ADVERTISING_ATTRIBUTION_QUERY_KEYS.has(normalized);
}

/** Remove paid/referral parameters while preserving every unrelated query. */
export function stripAdvertisingAttributionFromUrlString(
  value: string,
): string {
  const queryIndex = value.indexOf("?");
  if (queryIndex < 0) return value;
  const hashIndex = value.indexOf("#", queryIndex);
  const queryEnd = hashIndex < 0 ? value.length : hashIndex;
  const params = new URLSearchParams(value.slice(queryIndex + 1, queryEnd));
  let changed = false;
  for (const key of [...params.keys()]) {
    if (!ADVERTISING_ATTRIBUTION_QUERY_KEYS.has(key.toLowerCase())) continue;
    params.delete(key);
    changed = true;
  }
  if (!changed) return value;
  const query = params.toString();
  return `${value.slice(0, queryIndex)}${query ? `?${query}` : ""}${
    hashIndex < 0 ? "" : value.slice(hashIndex)
  }`;
}

/** Ensure an Analytics-only provider cannot observe paid URL identifiers. */
export function stripAdvertisingAttributionFromCurrentUrl(): void {
  if (typeof window === "undefined") return;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const scrubbed = stripAdvertisingAttributionFromUrlString(current);
  if (scrubbed === current) return;
  try {
    window.history.replaceState(window.history.state, "", scrubbed);
  } catch {
    // Final provider-dispatch sanitizers remain the fail-closed backstop.
  }
}

type Subscriber = (update: WebConsentLifecycleUpdate) => void;

const subscribers = new Set<Subscriber>();
let installed = false;
let timer: ReturnType<typeof setTimeout> | null = null;
let lastSignature: string | null = null;
let lastComplete: boolean | null = null;
const lastCategoryAuthority: Record<WebConsentCategory, string | null> = {
  analytics: null,
  advertising: null,
  personalization: null,
};
let emittingLifecycleEvent = false;

function safeStorageRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Browser cookies remain authoritative.
  }
}

function cookiePaths(): string[] {
  const segments = (window.location.pathname || "/").split("/").filter(Boolean);
  const paths = new Set<string>(["/"]);
  for (let index = 1; index <= segments.length; index += 1) {
    paths.add(`/${segments.slice(0, index).join("/")}`);
  }
  return [...paths];
}

function cookieDomains(): Array<string | null> {
  const hostname = window.location.hostname ?? "";
  const domains = new Set<string>();
  if (hostname && hostname !== "localhost") {
    domains.add(hostname);
    domains.add(`.${hostname}`);
  }
  if (
    hostname === "thesitebook.co.uk" ||
    hostname.endsWith(".thesitebook.co.uk")
  ) {
    domains.add("thesitebook.co.uk");
    domains.add(".thesitebook.co.uk");
  }
  return [null, ...domains];
}

export function deleteBrowserCookie(name: string): void {
  if (typeof window === "undefined") return;
  for (const path of cookiePaths()) {
    for (const domain of cookieDomains()) {
      const domainAttribute = domain ? `; Domain=${domain}` : "";
      document.cookie = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domainAttribute}; SameSite=Lax`;
    }
  }
}

/** Expire first-party identifiers for exactly one optional purpose. */
export function purgeOptionalConsentCookies(
  category: WebConsentCategory,
): void {
  if (typeof document === "undefined") return;
  const names = new Set(
    document.cookie
      .split(";")
      .map((part) => part.slice(0, Math.max(part.indexOf("="), 0)).trim())
      .filter(Boolean),
  );
  for (const known of [
    "ph_anon_id",
    "_ga",
    "_gcl_au",
    "_fbp",
    "_fbc",
    "_clck",
    "_clsk",
    "_uetmsclkid",
    "_uetsid",
    "_uetsid_exp",
    "_uetvid",
    "_uetvid_exp",
    "MUID",
    "MSPTC",
    "FPAU",
    "FPGCLAW",
    "FPGCLGB",
    "FPGCLGS",
    "FPGCLDC",
    "FPGSID",
    "tsb_compare_src",
  ]) {
    names.add(known);
  }
  for (const name of names) {
    if (isOptionalCookieForCategory(name, category)) {
      deleteBrowserCookie(name);
    }
  }

  const purgeStorage = (storage: Storage): void => {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (!key) continue;
      if (isOptionalCookieForCategory(key, category)) {
        storage.removeItem(key);
        continue;
      }
      if (
        category === "analytics" &&
        (key.startsWith("ph_") || key.toLowerCase().includes("clarity"))
      ) {
        storage.removeItem(key);
      }
      if (
        category === "advertising" &&
        (key.startsWith("tsb_attribution") ||
          key.startsWith("tsb_signup_attribution") ||
          key === "tsb_compare_src" ||
          key === "tsb_referral_code")
      ) {
        storage.removeItem(key);
      }
    }
  };
  for (const storageName of ["localStorage", "sessionStorage"] as const) {
    try {
      purgeStorage(window[storageName]);
    } catch {
      // Cookie deletion and the other storage area remain best-effort.
    }
  }
  if (category === "advertising") clearBrowserAttributionStorage();
}

export function disableOptionalProviders(category: WebConsentCategory): void {
  if (typeof window === "undefined") return;
  if (category === "analytics") {
    (window as unknown as Record<string, boolean>)[`ga-disable-${GA_ID}`] =
      true;
    window.gtag?.("consent", "update", { analytics_storage: "denied" });
    // Calling opt-out before PostHog has ever initialised can itself persist
    // an SDK preference marker. Before first acceptance, leave the SDK
    // untouched; once loaded, stop both capture and replay immediately.
    if (posthog.__loaded) {
      if (!posthog.has_opted_out_capturing?.()) {
        posthog.opt_out_capturing?.();
      }
      posthog.stopSessionRecording?.();
    }
    window.clarity?.("stop");
    window.clarity?.("consentv2", {
      ad_Storage: "denied",
      analytics_Storage: "denied",
    });
    return;
  }

  if (category === "personalization") {
    // Independent of Advertising: withdrawing personalisation must not touch
    // ad_storage/ad_user_data, the AW tag, UET, or Meta — those belong to the
    // Advertising purpose and a visitor may keep measurement while declining
    // personalisation/remarketing.
    window.gtag?.("consent", "update", { ad_personalization: "denied" });
    return;
  }

  (window as unknown as Record<string, boolean>)[`ga-disable-${AW_ID}`] = true;
  window.gtag?.("consent", "update", {
    ad_storage: "denied",
    ad_user_data: "denied",
  });
  window.__tsbMicrosoftUetSuppressed = true;
  if (Array.isArray(window.uetq)) {
    // bat.js has not loaded yet (it is injected at idle), so everything still
    // queued — a grant, a page load, a custom conversion event — would replay
    // the moment it loads. Drop it: a withdrawal before load sends nothing.
    window.uetq.length = 0;
    window.uetq.push("consent", "default", { ad_storage: "denied" });
    window.uetq.push("consent", "update", { ad_storage: "denied" });
  } else {
    window.uetq?.push("consent", "update", { ad_storage: "denied" });
  }
  window.fbq?.("consent", "revoke");
}

/**
 * Disable a loaded provider before deleting its identifiers. The microtask
 * pass catches a cookie write already queued by a provider in the same task.
 */
export function withdrawOptionalCategory(
  category: WebConsentCategory,
  options: { stripCurrentAdvertisingUrl?: boolean } = {},
): void {
  if (
    category === "advertising" &&
    options.stripCurrentAdvertisingUrl !== false
  ) {
    stripAdvertisingAttributionFromCurrentUrl();
  }
  disableOptionalProviders(category);
  purgeOptionalConsentCookies(category);
  // Same rule as the timed rechecks below: re-read the purpose first, so a
  // re-acceptance in the same task keeps the identifier its provider just
  // re-established.
  queueMicrotask(() => {
    if (typeof document === "undefined") return;
    if (!parseWebConsentSnapshot(document.cookie)[category].granted) {
      purgeOptionalConsentCookies(category);
    }
  });
  // A loaded third-party script can finish an already-queued cookie write
  // after its stop/denied command returns. Recheck a short bounded window so
  // UET/Clarity/PostHog cannot immediately recreate an identifier after
  // withdrawal. A rapid re-acceptance is respected by re-reading the current
  // purpose before every pass.
  for (const delayMs of [50, 250, 1_000]) {
    window.setTimeout(() => {
      // A recheck can outlive its environment (jsdom teardown between test
      // files); it must never assume the globals still exist.
      if (typeof document === "undefined") return;
      if (!parseWebConsentSnapshot(document.cookie)[category].granted) {
        purgeOptionalConsentCookies(category);
      }
    }, delayMs);
  }
}

/** Compatibility helper for fail-closed legacy migration. */
export function purgeAllOptionalConsentState(
  options: { stripCurrentAdvertisingUrl?: boolean } = {},
): void {
  withdrawOptionalCategory("analytics");
  withdrawOptionalCategory("advertising", options);
  withdrawOptionalCategory("personalization");
}

function clearExpiredBrowserMirrors(category: WebConsentCategory): void {
  const names = WEB_CONSENT_COOKIE_NAMES[category];
  safeStorageRemove(names.choice);
  safeStorageRemove(names.updatedAt);
}

function snapshotSignature(snapshot: WebConsentSnapshot): string {
  return (["analytics", "advertising", "personalization"] as const)
    .map((category) => {
      const value = snapshot[category];
      return `${value.choice ?? "-"}:${value.updatedAt?.getTime() ?? "-"}:${value.current ? "1" : "0"}`;
    })
    .join("|");
}

function scheduleExpiry(snapshot: WebConsentSnapshot): void {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  const nowMs = Date.now();
  const expiryTimes = (
    ["analytics", "advertising", "personalization"] as const
  ).flatMap((category) => {
    const updatedAt = snapshot[category].updatedAt;
    if (!snapshot[category].current || !updatedAt) return [];
    return [
      updatedAt.getTime() + WEB_CONSENT_COOKIE_MAX_AGE_SECONDS * 1000 + 1,
    ];
  });
  if (expiryTimes.length === 0) return;
  const remainingMs = Math.max(1, Math.min(...expiryTimes) - nowMs);
  timer = setTimeout(
    () => reconcileBrowserConsent("expiry"),
    Math.min(remainingMs, MAX_BROWSER_TIMEOUT_MS),
  );
}

function reconcileBrowserConsent(reason: WebConsentLifecycleReason): void {
  if (typeof document === "undefined") return;
  const snapshot = parseWebConsentSnapshot(document.cookie);
  for (const category of [
    "analytics",
    "advertising",
    "personalization",
  ] as const) {
    const categoryAuthority = `${snapshot[category].choice ?? "-"}:${snapshot[category].updatedAt?.getTime() ?? "-"}:${snapshot[category].granted ? "1" : "0"}`;
    if (
      !snapshot[category].granted &&
      categoryAuthority !== lastCategoryAuthority[category]
    ) {
      // An undecided visitor has not authorised any optional provider, so old
      // provider state must still be disabled and purged. Keep the current
      // landing query pending while the banner is open: it already exists in
      // the address bar and is not exposed to an optional provider. A current
      // explicit refusal strips immediately; Analytics-only provider loaders
      // independently strip before they initialise.
      withdrawOptionalCategory(category, {
        stripCurrentAdvertisingUrl:
          category !== "advertising" ||
          (snapshot.advertising.current &&
            snapshot.advertising.choice === "declined"),
      });
    }
    lastCategoryAuthority[category] = categoryAuthority;
    if (!snapshot[category].current) {
      clearExpiredBrowserMirrors(category);
    }
  }

  const signature = snapshotSignature(snapshot);
  const changed = signature !== lastSignature;
  const shouldReprompt = !snapshot.complete && lastComplete !== false;
  lastSignature = signature;
  lastComplete = snapshot.complete;
  scheduleExpiry(snapshot);

  if (changed || reason === "initial") {
    const update = { snapshot, reason };
    subscribers.forEach((subscriber) => subscriber(update));
    // An explicit event is already being delivered to every component. For
    // storage/focus/timer reconciliation, fan the canonical snapshot out on
    // the same compatibility event without recursively re-entering us.
    if (reason !== "explicit") {
      emittingLifecycleEvent = true;
      try {
        window.dispatchEvent(
          new CustomEvent(WEB_CONSENT_LIFECYCLE_EVENT, { detail: update }),
        );
      } finally {
        emittingLifecycleEvent = false;
      }
    }
  }
  if (shouldReprompt) {
    window.dispatchEvent(
      new CustomEvent(WEB_CONSENT_REPROMPT_EVENT, {
        detail: { snapshot, reason },
      }),
    );
  }
}

function isConsentStorageKey(key: string | null): boolean {
  return (["analytics", "advertising", "personalization"] as const).some(
    (category) => {
      const names = WEB_CONSENT_COOKIE_NAMES[category];
      return key === names.choice || key === names.updatedAt;
    },
  );
}

function onExplicitChange(): void {
  if (emittingLifecycleEvent) return;
  reconcileBrowserConsent("explicit");
}

function onStorage(event: StorageEvent): void {
  if (isConsentStorageKey(event.key)) reconcileBrowserConsent("storage");
}

function onFocus(): void {
  reconcileBrowserConsent("focus");
}

function onVisibility(): void {
  if (document.visibilityState === "visible") {
    reconcileBrowserConsent("visibility");
  }
}

function onLocationChange(): void {
  const snapshot = parseWebConsentSnapshot(document.cookie);
  if (
    (snapshot.advertising.current &&
      snapshot.advertising.choice === "declined") ||
    (snapshot.analytics.granted && !snapshot.advertising.granted)
  ) {
    stripAdvertisingAttributionFromCurrentUrl();
  }
}

function install(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener(CONSENT_CHANGE_EVENT, onExplicitChange);
  window.addEventListener("storage", onStorage);
  window.addEventListener("focus", onFocus);
  window.addEventListener(ANALYTICS_LOCATION_CHANGE_EVENT, onLocationChange);
  document.addEventListener("visibilitychange", onVisibility);
}

function uninstall(): void {
  if (!installed || typeof window === "undefined") return;
  window.removeEventListener(CONSENT_CHANGE_EVENT, onExplicitChange);
  window.removeEventListener("storage", onStorage);
  window.removeEventListener("focus", onFocus);
  window.removeEventListener(ANALYTICS_LOCATION_CHANGE_EVENT, onLocationChange);
  document.removeEventListener("visibilitychange", onVisibility);
  if (timer !== null) clearTimeout(timer);
  timer = null;
  installed = false;
  lastSignature = null;
  lastComplete = null;
  lastCategoryAuthority.analytics = null;
  lastCategoryAuthority.advertising = null;
  lastCategoryAuthority.personalization = null;
}

/**
 * Single browser-lifetime source of truth for every optional provider.
 * Timestamp-key storage changes cover same-choice renewal in another tab;
 * focus/visibility/timer checks prevent an old open tab outliving 180 days.
 */
export function subscribeToBrowserConsent(subscriber: Subscriber): () => void {
  if (typeof window === "undefined") return () => {};
  const wasEmpty = subscribers.size === 0;
  subscribers.add(subscriber);
  install();
  if (wasEmpty) {
    reconcileBrowserConsent("initial");
  } else {
    subscriber({
      snapshot: parseWebConsentSnapshot(document.cookie),
      reason: "initial",
    });
  }
  return () => {
    subscribers.delete(subscriber);
    if (subscribers.size === 0) uninstall();
  };
}

export function subscribeToBrowserConsentCategory(
  category: WebConsentCategory,
  callback: () => void,
): () => void {
  return subscribeToBrowserConsent(() => callback());
}
